// src/components/MatrixSVGChart.jsx
// ============================================================
// Shared SVG portfolio matrix chart.
// Extracted from MatrixPage to create a single source of truth
// for matrix rendering used by both MatrixPage and the
// Strategy Decision Canvas.
//
// Axis mapping (matches MatrixPage exactly):
//   X = Ease of Implementation (implementation_score / ie_axis)
//   Y = Business Value         (value_score / bv_axis)
//
// Quadrant colors (matches MatrixPage exactly):
//   Quick Win            (high BV, high ease)  → #22C55E
//   Strategic Investment (high BV, low ease)   → #1D4E89
//   Efficiency Booster   (low BV,  high ease)  → #FF6E4C
//   Resource Drain       (low BV,  low ease)   → #C2410C
//
// Props:
//   items        — Array<{ id, x, y, label, rank? }>
//                  x/y are already in axisScale coordinate space
//   axisScale    — { minX, maxX, minY, maxY }
//   selectedId   — string | null
//   highlightedId— string | null
//   onSelect     — (id: string) => void
//   onHover      — (id: string | null) => void   (optional)
//   size         — number (px, default 400)
//   className    — string (optional)
// ============================================================

import React from 'react';

const PLOT_PADDING = 3;
const INNER_MIN    = PLOT_PADDING;
const INNER_MAX    = 100 - PLOT_PADDING;
const INNER_SPAN   = INNER_MAX - INNER_MIN;

// Quadrant colors — identical to MatrixPage core-category mode
const quadrantColor = (x, y, midX, midY) => {
  const highBV  = y >= midY;
  const highEOI = x >= midX;
  if (highBV  && highEOI)  return '#22C55E'; // Quick Win
  if (highBV  && !highEOI) return '#1D4E89'; // Strategic Investment
  if (!highBV && highEOI)  return '#FF6E4C'; // Efficiency Booster
  return '#C2410C';                           // Resource Drain
};

// Map a data value into SVG coordinate space
const toSVGX = (val, minX, maxX) =>
  INNER_MIN + ((val - minX) / (maxX - minX)) * INNER_SPAN;

const toSVGY = (val, minY, maxY) =>
  INNER_MAX - ((val - minY) / (maxY - minY)) * INNER_SPAN;

export default function MatrixSVGChart({
  items        = [],
  axisScale    = { minX: 20, maxX: 90, minY: 20, maxY: 90 },
  selectedId   = null,
  highlightedId = null,
  onSelect     = () => {},
  onHover      = null,
  size         = 400,
  className    = '',
}) {
  const { minX, maxX, minY, maxY } = axisScale;

  // Quadrant divider midpoints (p50 of scale)
  const midXVal = (minX + maxX) / 2;
  const midYVal = (minY + maxY) / 2;

  // Divider lines in SVG coordinate space
  const xMid = toSVGX(midXVal, minX, maxX);
  const yMid = toSVGY(midYVal, minY, maxY);

  return (
    <div
      className={className}
      style={{ width: size, height: size, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
    >
      <svg
        width="100%"
        height="100%"
        viewBox="0 0 100 100"
        preserveAspectRatio="xMidYMid meet"
        style={{
          display: 'block',
          width: '100%',
          height: '100%',
          border: '1px solid #e5e7eb',
          borderRadius: 4,
          background: '#fff',
        }}
        onClick={(e) => {
          if (e.target.tagName === 'svg') onSelect(null);
        }}
      >
        {/* Quadrant corner labels */}
        <text x="3"  y="4.5"  fontSize="1.8" fill="#6b7280" fontWeight="600">
          Strategic Investments
        </text>
        <text x="97" y="4.5"  fontSize="1.8" fill="#6b7280" fontWeight="600" textAnchor="end">
          Quick Wins
        </text>
        <text x="3"  y="98.5" fontSize="1.8" fill="#6b7280" fontWeight="600">
          Resource Drains
        </text>
        <text x="97" y="98.5" fontSize="1.8" fill="#6b7280" fontWeight="600" textAnchor="end">
          Efficiency Boosters
        </text>

        {/* Quadrant divider lines */}
        <line x1={xMid} y1={INNER_MIN} x2={xMid} y2={INNER_MAX} stroke="#d1d5db" strokeWidth="0.6" />
        <line x1={INNER_MIN} y1={yMid} x2={INNER_MAX} y2={yMid} stroke="#d1d5db" strokeWidth="0.6" />

        {/* Axis labels */}
        <text x="50" y="99" fontSize="1.3" fill="#6b7280" textAnchor="middle">
          Ease of Implementation P50
        </text>
        <text
          x="1" y="50" fontSize="1.3" fill="#6b7280" textAnchor="middle"
          transform="rotate(-90 1 50)"
        >
          Business Value P50
        </text>

        {/* Points */}
        {items.map((item) => {
          const isSelected    = item.id === selectedId;
          const isHighlighted = item.id === highlightedId;

          // Clamp to axis range
          const rawX = Math.max(minX, Math.min(maxX, item.x));
          const rawY = Math.max(minY, Math.min(maxY, item.y));

          let cx = toSVGX(rawX, minX, maxX);
          let cy = toSVGY(rawY, minY, maxY);

          const r       = isSelected ? 1.5 : isHighlighted ? 1.2 : 0.9;
          const strokeW = isSelected ? 1.2 : isHighlighted ? 1.0 : 0.8;
          const opacity = isSelected || isHighlighted ? 1 : 0.9;

          const color = quadrantColor(rawX, rawY, midXVal, midYVal);

          // Keep point fully within bounds
          const margin = r + (strokeW * 0.5) + 1.4;
          cx = Math.max(INNER_MIN + margin, Math.min(INNER_MAX - margin, cx));
          cy = Math.max(INNER_MIN + margin, Math.min(INNER_MAX - margin, cy));

          return (
            <g
              key={item.id}
              onClick={(e) => { e.stopPropagation(); onSelect(item.id); }}
              onMouseEnter={() => onHover?.(item.id)}
              onMouseLeave={() => onHover?.(null)}
              style={{ cursor: 'pointer' }}
            >
              {/* Selection pulse ring */}
              {isSelected && (
                <circle
                  cx={cx} cy={cy} r={r + 3}
                  fill="none" stroke={color} strokeWidth="1.2" opacity="0.3"
                  style={{ animation: 'pulse 1.5s ease-in-out infinite' }}
                />
              )}
              {/* Hover ring */}
              {isHighlighted && !isSelected && (
                <circle
                  cx={cx} cy={cy} r={r + 2}
                  fill="none" stroke="#18365E" strokeWidth="0.8" opacity="0.35"
                />
              )}
              {/* Main point */}
              <circle
                cx={cx} cy={cy} r={r}
                fill={color}
                stroke={isSelected || isHighlighted ? '#1f2937' : color}
                strokeWidth={strokeW}
                opacity={opacity}
                style={{ transition: 'all 0.25s ease' }}
              >
                <title>
                  {[
                    item.label,
                    item.rank != null ? `Rank: #${item.rank}` : null,
                    `BV: ${rawY.toFixed(1)}`,
                    `EOI: ${rawX.toFixed(1)}`,
                  ].filter(Boolean).join('\n')}
                </title>
              </circle>
              {/* Rank badge on point (only for small sets like top-10 canvas) */}
              {item.rank != null && item.rank <= 20 && (
                <text
                  x={cx} y={cy + (r * 0.38)}
                  textAnchor="middle"
                  fontSize={r * 1.1}
                  fontWeight="800"
                  fill="white"
                  style={{ pointerEvents: 'none' }}
                >
                  {item.rank}
                </text>
              )}
            </g>
          );
        })}

        {/* CSS animation for selection pulse */}
        <style>{`
          @keyframes pulse {
            0%,100% { opacity:.25; transform:scale(1); }
            50%      { opacity:.4;  transform:scale(1.05); }
          }
        `}</style>
      </svg>
    </div>
  );
}